import React, { useContext, useEffect, useRef } from 'react';
import { Plus, User } from 'tabler-icons-react';
import { Context as RegistrationAdminContext } from '../../../../providers/RegistrationAdminProvider';
import { useRegAssociationAdminPermissions } from '../../../../helpers/adminHelper';
import { EVALUATION_VIEW_TABS } from '../navigation/links';
import AdminView from '../../../../components/common/AdminView';
import { useModalState, usePaginationFilter } from '../../../../helpers/hooks';
import TableView from '../../../../components/common/TableView';
import AppCard from '../../../../components/common/AppCard';
import { triggerNotification } from '../../../../helpers/notificationHelper';
import PlayerStatsModal from '../../../../components/content/players/PlayerStatsModal';
import EvaluationPlayerModal from '../../../../components/content/evaluation/EvaluationPlayerModal';
import PlayerStatsSummaryModal from '../../../../components/content/players/PlayerStatsSummaryModal';

const VIEW_ACTIONS_ENUM = {
  view: 'VIEW',
  create: 'CREATE',
  delete: 'DELETE'
};

const VIEW_ACTIONS = [
  {
    label: 'View',
    value: VIEW_ACTIONS_ENUM.view
  },
  {
    label: 'Edit',
    value: VIEW_ACTIONS_ENUM.create,
    isCustomOnly: true
  },
  {
    label: 'Delete',
    value: VIEW_ACTIONS_ENUM.delete,
    isCustomOnly: true
  }
];

const TABLE_COLUMNS = [
  {
    label: 'Name',
    value: 'name',
    sortable: true
  },
  {
    label: 'Evaluations',
    value: 'evaluations',
    sortable: true
  },
  {
    label: 'Overall Score',
    value: 'overall_score',
    sortable: true
  }
];

const PLAYER_TYPES = [
  {
    label: 'Registrant',
    value: 'registrant'
  },
  {
    label: 'Custom',
    value: 'custom'
  }
];

const EvaluationPlayersTable = () => {
  const hasFetched = useRef(false);
  const fetchedPkRegAssociation = useRef(false);
  const { state: modalState, onOpenModal, onCloseModal } = useModalState();
  const { state, fetchPaginatedRegScoutReportPerson } = useContext(
    RegistrationAdminContext
  );
  const { regAssociation } = useRegAssociationAdminPermissions();
  const {
    pageIndex,
    pageSize,
    totalCount,
    totalPages,
    data,
    filter,
    loading: dataLoading,
    isDescendingSort,
    onPageChange,
    onFilterChange,
    onSortChange,
    onRefresh
  } = usePaginationFilter(
    {
      search: '',
      fkRegAssociationDivision: null
    },
    (newFilter) => {
      fetchPaginatedRegScoutReportPerson(
        regAssociation.pkRegAssociation,
        newFilter,
        null,
        (error) => {
          triggerNotification(error);
        }
      );
    },
    state.paginatedRegScoutReportPersons
  );
  const loading = !hasFetched.current || dataLoading;

  const regAssociationDivisionOptions =
    state.regScoutReportEvaluationOptions.value &&
    state.regScoutReportEvaluationOptions.value?.pkRegAssociation ===
      regAssociation?.pkRegAssociation
      ? state.regScoutReportEvaluationOptions.value.regAssociationDivisions
          .filter((d) => d.hasEvaluationSessions)
          .map((d) => ({
            label: d.label,
            value: d.value.toString()
          }))
          .sort((a, b) => a.label.localeCompare(b.label))
      : [];

  useEffect(() => {
    if (regAssociation) {
      if (
        state.regScoutReportEvaluationOptions.value?.pkRegAssociation ===
          regAssociation.pkRegAssociation &&
        fetchedPkRegAssociation.current !== regAssociation.pkRegAssociation
      ) {
        onFilterChange(
          {
            fkRegAssociationDivision: regAssociationDivisionOptions[0].value,
            playerType: '',
            search: ''
          },
          true
        );
        fetchedPkRegAssociation.current = regAssociation.pkRegAssociation;
      }

      if (state.regScoutReportEvaluationOptions.lastUpdated) {
        hasFetched.current = true;
      }
    }
  }, [state.regScoutReportEvaluationOptions.value?.pkRegAssociation]);

  return (
    <AdminView
      actionControl={{
        label: 'Create Player',
        icon: <Plus />,
        action: 'CREATE'
      }}
      headerTitle="Scouting & Evaluations"
      onAction={onOpenModal}
      tabs={EVALUATION_VIEW_TABS}
    >
      <AppCard style={{ flex: 1, padding: 0, height: '100%' }}>
        <TableView
          columns={TABLE_COLUMNS}
          disabledContent={{
            title: 'No players created',
            description:
              'Once users have completed registration, they will appear here as players. You can also add custom players manually.',
            icon: <User color="#000" size={125} />,
            primaryButton: {
              label: 'Create Player',
              icon: <Plus size={18} />,
              onClick: () => onOpenModal(VIEW_ACTIONS_ENUM.create)
            }
          }}
          emptyMessage="No players found."
          enableInputsWhenLoading
          filters={[
            {
              key: 1,
              label: 'Division',
              type: 'select',
              value: filter.fkRegAssociationDivision,
              placeholder: 'Filter by division',
              data: regAssociationDivisionOptions,
              searchable: true,
              onChange: (value) => {
                if (value) {
                  onFilterChange(
                    {
                      ...filter,
                      fkRegAssociationDivision: value
                    },
                    true
                  );
                }
              }
            },
            {
              key: 2,
              label: 'Player Type',
              type: 'select',
              value: filter.playerType,
              placeholder: 'Filter by type',
              data: PLAYER_TYPES,
              clearable: true,
              searchable: true,
              onChange: (value) => {
                onFilterChange(
                  {
                    ...filter,
                    playerType: value
                  },
                  true
                );
              }
            },
            {
              key: 3,
              label: 'Player Name',
              type: 'text',
              value: filter.search,
              placeholder: 'Filter by player',
              onChange: (value) => onFilterChange({ ...filter, search: value })
            }
          ]}
          isDescendingSort={isDescendingSort}
          isLoading={loading}
          noSort
          onAction={(action, item) => {
            onOpenModal(
              action,
              data.find((f) => f.entityId === item.key)
            );
          }}
          onChangeSortBy={onSortChange}
          onPageChange={onPageChange}
          onRefresh={onRefresh}
          pageIndex={pageIndex}
          rows={data.map((p) => ({
            key: p.entityId,
            actions: VIEW_ACTIONS.filter(
              (f) => !f.isCustomOnly || p.person.isCustomPerson
            ),
            columns: [
              {
                key: 1,
                label: `${p.person.firstName} ${p.person.lastName}`,
                subLabel: p.regAssociationDivision.name,
                weight: 500,
                icon: <User color="black" size="30" />,
                onClick: () => {
                  onOpenModal(VIEW_ACTIONS_ENUM.view, p);
                }
              },
              {
                key: 3,
                label: p.evaluationCount,
                showOnMobile: true
              },
              {
                key: 4,
                label: p.overallScore,
                showOnMobile: true
              }
            ]
          }))}
          sortBy={filter.sort}
          tableTitle="Evaluation Players"
          totalCount={totalCount}
          totalPages={totalPages}
        />

        <PlayerStatsSummaryModal
          isOpen={
            modalState.isOpen && modalState.action === VIEW_ACTIONS_ENUM.view
          }
          onClose={onCloseModal}
          player={modalState.item}
          regAssociation={regAssociation}
        />
        <EvaluationPlayerModal
          fkRegAssociationDivision={filter.fkRegAssociationDivision}
          isOpen={
            modalState.isOpen && modalState.action !== VIEW_ACTIONS_ENUM.view
          }
          onClose={onCloseModal}
          onSave={() => {
            onCloseModal();
            onRefresh();
          }}
          pkRegAssociation={regAssociation?.pkRegAssociation}
          regScoutReportPerson={modalState.item}
          showDelete={modalState.action === VIEW_ACTIONS_ENUM.delete}
        />
      </AppCard>
    </AdminView>
  );
};

export default EvaluationPlayersTable;
